export const PLAYER_TAB_VALUES = {
    CONSENSUS: "consensus",
    DRAFT_ODDS: "draft-odds",
    STATS: "stats",
    FILM: "film",
    COMPARE: "compare",
    SCOUTING_REPORTS: "scouting-reports",
    DISCUSSION: "discussion",
};

export const DEFAULT_PLAYER_TAB = PLAYER_TAB_VALUES.CONSENSUS;

export const VALID_PLAYER_TABS = new Set(Object.values(PLAYER_TAB_VALUES));

const TAB_META = {
    [PLAYER_TAB_VALUES.CONSENSUS]: {
        label: "Consensus",
        description: (firstName, lastName) =>
            `Track ${firstName} ${lastName}'s consensus ranking history, scout rankings, and draft stock on Hockey Pipeline.`,
    },
    [PLAYER_TAB_VALUES.DRAFT_ODDS]: {
        label: "Draft Odds",
        description: (firstName, lastName) =>
            `Simulated NHL draft odds and pick probabilities for ${firstName} ${lastName} on Hockey Pipeline.`,
    },
    [PLAYER_TAB_VALUES.STATS]: {
        label: "Stats",
        description: (firstName, lastName) =>
            `Draft-year statistics and performance data for ${firstName} ${lastName} on Hockey Pipeline.`,
    },
    [PLAYER_TAB_VALUES.FILM]: {
        label: "Film",
        description: (firstName, lastName) =>
            `Scouting video and film breakdown for ${firstName} ${lastName} on Hockey Pipeline.`,
    },
    [PLAYER_TAB_VALUES.COMPARE]: {
        label: "Compare",
        description: (firstName, lastName) =>
            `Compare ${firstName} ${lastName}'s stats and rankings against other prospects on Hockey Pipeline.`,
    },
    [PLAYER_TAB_VALUES.SCOUTING_REPORTS]: {
        label: "Scouting Reports",
        description: (firstName, lastName) =>
            `Read and write community scouting reports for ${firstName} ${lastName} on Hockey Pipeline.`,
    },
    [PLAYER_TAB_VALUES.DISCUSSION]: {
        label: "Discussion",
        description: (firstName, lastName) =>
            `Join the discussion about ${firstName} ${lastName} on Hockey Pipeline.`,
    },
    arrival: {
        label: "NHL Arrival",
        description: (firstName, lastName) =>
            `NHL arrival outlook and draft-stage probabilities for ${firstName} ${lastName} on Hockey Pipeline.`,
    },
    "nhl-stats": {
        label: "NHL Career Stats",
        description: (firstName, lastName) =>
            `NHL career statistics for ${firstName} ${lastName} on Hockey Pipeline.`,
    },
};

export const POST_DRAFT_TABS = new Set(["arrival", "nhl-stats"]);

export function normalizePostDraftTab(tab) {
    if (tab === "nhl-stats" || tab === "stats") return "nhl-stats";
    return "arrival";
}

export function normalizePlayerTab(tab, { hasFilm = true, isPostDraftView = false } = {}) {
    if (isPostDraftView) {
        return normalizePostDraftTab(tab);
    }
    if (!tab || !VALID_PLAYER_TABS.has(tab) || POST_DRAFT_TABS.has(tab)) {
        return DEFAULT_PLAYER_TAB;
    }
    if (tab === PLAYER_TAB_VALUES.FILM && !hasFilm) {
        return DEFAULT_PLAYER_TAB;
    }
    return tab;
}

export function buildPlayerShareParams(shareState = {}) {
    const params = new URLSearchParams();
    const view = shareState.view || "pre-draft";
    if (view === "post-draft") {
        params.set("view", "post-draft");
        const tab = normalizePostDraftTab(shareState.tab);
        params.set("tab", tab);
        if (tab === "nhl-stats") {
            if (shareState.display === "graph") {
                params.set("display", "graph");
                const prefs = shareState.graphPrefs || {};
                if (prefs.selectedMetrics?.length) {
                    params.set("metrics", prefs.selectedMetrics.join(","));
                }
                if (prefs.chartType && prefs.chartType !== "line") {
                    params.set("chart", prefs.chartType === "area" ? "line" : prefs.chartType);
                }
                if (prefs.seasonMode && prefs.seasonMode !== "combined") {
                    params.set("seasons", prefs.seasonMode);
                }
                if (prefs.valueMode && prefs.valueMode !== "totals") {
                    params.set("values", prefs.valueMode);
                }
            }
            if (shareState.basis === "nhle") {
                params.set("basis", "nhle");
            }
        } else {
            if (shareState.metric === "rank") {
                params.set("metric", "rank");
            }
            if (shareState.stages?.length) {
                params.set("stages", shareState.stages.join(","));
            }
        }
        return params;
    }
    const tab = shareState.tab;
    if (tab && tab !== DEFAULT_PLAYER_TAB && !POST_DRAFT_TABS.has(tab)) {
        params.set("tab", tab);
    }
    return params;
}

export function buildPlayerPagePath(playerId, tabOrState = DEFAULT_PLAYER_TAB) {
    if (!playerId) return "/players";
    if (tabOrState && typeof tabOrState === "object") {
        const qs = buildPlayerShareParams(tabOrState).toString();
        return qs ? `/player/${playerId}?${qs}` : `/player/${playerId}`;
    }
    if (!tabOrState || tabOrState === DEFAULT_PLAYER_TAB) {
        return `/player/${playerId}`;
    }
    return `/player/${playerId}?tab=${encodeURIComponent(tabOrState)}`;
}

export function buildPlayerPageUrl(playerId, tabOrState = DEFAULT_PLAYER_TAB, origin = "https://hockeypipeline.com") {
    return `${origin}${buildPlayerPagePath(playerId, tabOrState)}`;
}

export function buildSharePlayerUrl(playerId, tabOrState = DEFAULT_PLAYER_TAB, origin = "https://hockeypipeline.com") {
    if (!playerId) return origin;
    if (tabOrState && typeof tabOrState === "object") {
        const qs = buildPlayerShareParams(tabOrState).toString();
        return qs ? `${origin}/share/players/${playerId}?${qs}` : `${origin}/share/players/${playerId}`;
    }
    if (!tabOrState || tabOrState === DEFAULT_PLAYER_TAB) {
        return `${origin}/share/players/${playerId}`;
    }
    return `${origin}/share/players/${playerId}?tab=${encodeURIComponent(tabOrState)}`;
}

export function buildShareCardUrl(playerId, shareState, origin = "https://hockeypipeline.com") {
    if (!playerId) return origin;
    const qs = buildPlayerShareParams(shareState || {}).toString();
    return qs ? `${origin}/share/players/${playerId}/card?${qs}` : `${origin}/share/players/${playerId}/card`;
}

export function getPlayerTabMeta(tab, player, { isPostDraftView = false } = {}) {
    const normalizedTab = normalizePlayerTab(tab, { isPostDraftView });
    const config = TAB_META[normalizedTab] || TAB_META[DEFAULT_PLAYER_TAB];
    const firstName = player?.first_name || "";
    const lastName = player?.last_name || "";
    const name = `${firstName} ${lastName}`.trim() || "Player";

    if (normalizedTab === DEFAULT_PLAYER_TAB) {
        return {
            title: `${name} - Hockey Pipeline`,
            description: config.description(firstName, lastName),
        };
    }

    return {
        title: `${name} — ${config.label} | Hockey Pipeline`,
        description: config.description(firstName, lastName),
    };
}
